import { inject, injectable } from 'inversify'
import { setup } from 'xstate'
import { TYPES } from '@/plugins/cad/module/container/types'
import type {
  KeyEventInfoHandler,
  KeyEventParams,
  MouseEventInfoHandler,
  MouseEventParams,
} from '@/plugins/cad/module/core/common/event'
import type { DetectorEventHandler } from '@/plugins/cad/module/core/selector/Detector'
import { SelectMode, type SelectorEventHandler } from '@/plugins/cad/module/core/selector/Selector'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import { KeyCodes } from '@/shared/constants'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import { CloseEntityCommand } from '../command/CloseEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import { EntityType } from '../model/common/constant'
import type { IEntity } from '../model/entity'
import type { EntitySelector } from '../selector/entitySelector'

const CLOSE_RESULT_DISPLAY_DURATION = 2000

const closeEntityMachine = setup({
  actions: {
    entrySelectEntity: () => {},
    exitSelectEntity: () => {},
    entryExecute: () => {},
  },
}).createMachine({
  id: 'closeEntity',
  initial: 'idle',
  states: {
    idle: {
      on: { NEXT: 'selectEntity' },
    },
    selectEntity: {
      entry: { type: 'entrySelectEntity' },
      exit: { type: 'exitSelectEntity' },
      on: { NEXT: 'execute' },
    },
    execute: {
      entry: { type: 'entryExecute' },
      on: { NEXT: 'selectEntity' },
    },
  },
})

/**
 * 闭合实体工具：将选中的多段线设置为闭合状态。
 */
@injectable()
export class CloseEntityTool
  extends StateMachineTool<typeof closeEntityMachine>
  implements
    MouseEventInfoHandler,
    KeyEventInfoHandler,
    SelectorEventHandler<IEntity>,
    DetectorEventHandler<IEntity>
{
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.CloseEntityTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  protected machine = closeEntityMachine.provide({
    actions: {
      entrySelectEntity: () => this.entrySelectEntity(),
      exitSelectEntity: () => this.exitSelectEntity(),
      entryExecute: () => this.entryExecute(),
    },
  })

  private selectedEntities: IEntity[] = []
  private timer: ReturnType<typeof setTimeout> | null = null
  private currentCommand: CloseEntityCommand | null = null

  protected onInitialize(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
      this.timer = null
    }
    this.entitySelector.clearSelection()
    this.entitySelector.config({ enableFrameSelect: false, mode: SelectMode.replace }) // 关闭框选功能，仅支持同时选中一个图元

    this.currentCommand = new CloseEntityCommand(this.viewManager)
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.entitySelector.clearSelection()
    this.entitySelector.resetConfig()
    this.historyManager.commitCommand()
    if (!this.timer) {
      this.mouseTipHelper.removeTip(MouseTipId.Edit)
    }
    this.selectedEntities = []
    this.currentCommand = null
  }

  /**
   * 鼠标按下：右键在选择阶段推进到执行阶段。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    // 右键
    if (params.event.button === 2 && this.checkState('selectEntity')) {
      this.send({ type: 'NEXT' })
    }
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /**
   * 选择变化回调：记录当前待闭合的实体集合。
   *
   * @param entities - 当前已选中的实体
   */
  onSelectorChanged(entities: IEntity[]) {
    this.selectedEntities = entities
  }

  /**
   * 实体探测过滤：仅保留多段线。
   *
   * @param entities - 候选实体列表
   * @returns 过滤后可闭合的实体列表
   */
  onDetectorFilter(entities: IEntity[]) {
    return entities.filter((entity) => [EntityType.Polyline].includes(entity.type))
  }

  /************ 状态机状态处理 ************/
  private entrySelectEntity() {
    this.inject(this.entitySelector)
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.close.target)
  }

  private exitSelectEntity() {
    this.eject(this.entitySelector)
  }

  private entryExecute() {
    if (this.selectedEntities.length === 0) {
      this.send({ type: 'NEXT' })
    } else {
      this.executeOperate()
    }
  }

  /************ 数据处理 ************/
  private executeOperate() {
    this.currentCommand?.addEntities(this.selectedEntities)
    this.selectedEntities = []
    this.entitySelector.clearSelection()
    this.showResultTip()
  }

  private showResultTip(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer)
    }
    this.mouseTipHelper.addTip(MouseTipId.Edit, CadTipKey.edit.close.success)
    this.timer = setTimeout(() => {
      this.mouseTipHelper.removeTip(MouseTipId.Edit, CadTipKey.edit.close.success)
      this.timer = null
      this.send({ type: 'NEXT' })
    }, CLOSE_RESULT_DISPLAY_DURATION)
  }
}
